/**
 * Health Score Gauge Component
 * Circular gauge showing financial health score
 */
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface HealthScoreGaugeProps {
  score: number;
  trend?: 'UP' | 'STABLE' | 'DOWN';
  size?: 'sm' | 'md' | 'lg';
}

export function HealthScoreGauge({ score, trend, size = 'md' }: HealthScoreGaugeProps) {
  const { t } = useTranslation();
  const normalizedScore = Math.max(0, Math.min(100, score));

  // Calculate stroke dash for circular progress
  const radius = size === 'lg' ? 60 : size === 'md' ? 50 : 40;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (normalizedScore / 100) * circumference;

  // Determine color based on score
  const getScoreColor = () => {
    if (normalizedScore >= 80) return { stroke: '#14B8A6', text: 'text-teal-600', bg: 'bg-teal-50' };
    if (normalizedScore >= 60) return { stroke: '#22C55E', text: 'text-green-600', bg: 'bg-green-50' };
    if (normalizedScore >= 40) return { stroke: '#EAB308', text: 'text-yellow-600', bg: 'bg-yellow-50' };
    if (normalizedScore >= 20) return { stroke: '#F97316', text: 'text-orange-600', bg: 'bg-orange-50' };
    return { stroke: '#EF4444', text: 'text-red-600', bg: 'bg-red-50' };
  };

  const colors = getScoreColor();
  const svgSize = size === 'lg' ? 160 : size === 'md' ? 140 : 100;
  const strokeWidth = size === 'lg' ? 12 : size === 'md' ? 10 : 8;

  const getScoreLabel = () => {
    if (normalizedScore >= 80) return t('treasury.healthScore.excellent');
    if (normalizedScore >= 60) return t('treasury.healthScore.good');
    if (normalizedScore >= 40) return t('treasury.healthScore.average');
    if (normalizedScore >= 20) return t('treasury.healthScore.poor');
    return t('treasury.healthScore.critical');
  };

  const TrendIcon = trend === 'UP' ? TrendingUp : trend === 'DOWN' ? TrendingDown : Minus;
  const trendColor = trend === 'UP' ? 'text-green-500' : trend === 'DOWN' ? 'text-red-500' : 'text-gray-400';

  return (
    <div className="flex flex-col items-center">
      {/* Gauge */}
      <div className="relative" style={{ width: svgSize, height: svgSize }}>
        <svg
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${svgSize} ${svgSize}`}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-3xl font-bold ${colors.text}`}>
            {Math.round(normalizedScore)}
          </span>
          <span className="text-xs text-gray-500 font-medium">/100</span>
        </div>
      </div>

      {/* Label and trend */}
      <div className="mt-3 text-center">
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text}`}>
          {getScoreLabel()}
        </span>
        {trend && (
          <div className={`flex items-center justify-center gap-1 mt-2 ${trendColor}`}>
            <TrendIcon className="w-4 h-4" />
            <span className="text-xs font-medium">
              {trend === 'UP' ? t('treasury.healthScore.trendUp', 'En hausse') : trend === 'DOWN' ? t('treasury.healthScore.trendDown', 'En baisse') : t('treasury.healthScore.trendStable', 'Stable')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default HealthScoreGauge;
